import { useState, useEffect, useCallback } from 'react';

// Cookie categories
export enum CookieCategory {
    ESSENTIAL = 'essential',
    FUNCTIONAL = 'functional',
    ANALYTICS = 'analytics',
    MARKETING = 'marketing'
}

// Cookie consent state interface
export interface CookieConsent {
    essential: boolean;
    functional: boolean;
    analytics: boolean;
    marketing: boolean;
    hasConsented: boolean;
    consentDate?: Date;
}

// Default consent state (only essential cookies are enabled by default)
const defaultConsent: CookieConsent = {
    essential: true, // Always true - essential cookies cannot be disabled
    functional: false,
    analytics: false,
    marketing: false,
    hasConsented: false
};

// Local storage key
const CONSENT_STORAGE_KEY = 'anathoth_cookie_consent';

/**
 * Custom hook for managing cookie consent
 */
export const useCookieConsent = () => {
    const [consent, setConsent] = useState<CookieConsent>(defaultConsent);
    const [showBanner, setShowBanner] = useState(false);

    // Load consent from localStorage on mount
    useEffect(() => {
        const storedConsent = localStorage.getItem(CONSENT_STORAGE_KEY);
        if (storedConsent) {
            try {
                const parsedConsent = JSON.parse(storedConsent);
                setConsent({
                    ...parsedConsent,
                    essential: true, // Ensure essential is always true
                    consentDate: parsedConsent.consentDate ? new Date(parsedConsent.consentDate) : undefined
                });
            } catch (error) {
                console.error('Error parsing cookie consent from localStorage:', error);
                setShowBanner(true);
            }
        } else {
            // No consent found, show banner
            setShowBanner(true);
        }
    }, []);

    // Save consent to localStorage
    const saveConsent = useCallback((newConsent: Partial<CookieConsent>, finalize: boolean = true) => {
        const updatedConsent: CookieConsent = {
            ...consent,
            ...newConsent,
            essential: true, // Essential cookies are always enabled
            hasConsented: finalize ? true : consent.hasConsented,
            consentDate: finalize ? new Date() : consent.consentDate
        };

        setConsent(updatedConsent);
        localStorage.setItem(CONSENT_STORAGE_KEY, JSON.stringify(updatedConsent));
        if (finalize) {
            setShowBanner(false);
        }

        // Trigger custom event for other parts of the app to listen to
        window.dispatchEvent(new CustomEvent('cookieConsentUpdated', { 
            detail: updatedConsent 
        }));
    }, [consent]);

    // Accept all cookies
    const acceptAll = useCallback(() => {
        saveConsent({
            functional: true,
            analytics: true,
            marketing: true
        }, true);
    }, [saveConsent]);

    // Accept only essential cookies
    const acceptEssential = useCallback(() => {
        saveConsent({
            functional: false,
            analytics: false,
            marketing: false
        }, true);
    }, [saveConsent]);

    // Update specific category (do not finalize consent or hide banner)
    const updateCategory = useCallback((category: CookieCategory, enabled: boolean) => {
        if (category === CookieCategory.ESSENTIAL) {
            // Essential cookies cannot be disabled
            return;
        }
        saveConsent({
            [category]: enabled
        }, false);
    }, [saveConsent]);

    // Reset consent (for testing or user request)
    const resetConsent = useCallback(() => {
        localStorage.removeItem(CONSENT_STORAGE_KEY);
        setConsent(defaultConsent);
        setShowBanner(true);
    }, []);

    // Check if a specific category is enabled
    const isCategoryEnabled = useCallback((category: CookieCategory): boolean => {
        return consent[category] || false;
    }, [consent]);

    // Hide banner manually
    const hideBanner = useCallback(() => {
        setShowBanner(false);
    }, []);

    // Show banner manually (for settings)
    const showConsentBanner = useCallback(() => {
        setShowBanner(true);
    }, []);

    return {
        consent,
        showBanner,
        acceptAll,
        acceptEssential,
        updateCategory,
        resetConsent,
        isCategoryEnabled,
        hideBanner,
        showConsentBanner,
        saveConsent
    };
};

// Cookie utility functions
export const cookieUtils = {
    // Set a cookie with proper consent check
    setCookie: (name: string, value: string, days: number, category: CookieCategory = CookieCategory.ESSENTIAL) => {
        const consent = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || '{}');
        
        // Only set cookie if the category is consented to
        if (consent[category] === true || category === CookieCategory.ESSENTIAL) {
            const expires = new Date();
            expires.setTime(expires.getTime() + (days * 24 * 60 * 60 * 1000));
            document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax;Secure`;
        }
    },

    // Get a cookie value
    getCookie: (name: string): string | null => {
        const nameEQ = name + "=";
        const ca = document.cookie.split(';');
        for (let i = 0; i < ca.length; i++) {
            let c = ca[i];
            while (c.charAt(0) === ' ') c = c.substring(1, c.length);
            if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
        }
        return null;
    },

    // Delete a cookie
    deleteCookie: (name: string) => {
        document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
    },

    // Clear all non-essential cookies based on current consent
    clearNonConsentedCookies: () => {
        const consent = JSON.parse(localStorage.getItem(CONSENT_STORAGE_KEY) || '{}');
        
        // List of known cookies by category
        const cookiesByCategory = {
            [CookieCategory.ANALYTICS]: ['_ga', '_gid', '_gat', '_gtag', 'hjid', 'hjFirstSeen'],
            [CookieCategory.MARKETING]: ['_fbp', '_fbc', 'fr', 'sb', 'test_cookie'],
            [CookieCategory.FUNCTIONAL]: ['theme', 'language', 'preferences']
        };

        // Clear cookies for non-consented categories
        Object.entries(cookiesByCategory).forEach(([category, cookies]) => {
            if (!consent[category]) {
                cookies.forEach(cookieName => {
                    cookieUtils.deleteCookie(cookieName);
                });
            }
        });
    }
};

// Google Analytics integration
export const gtag = (...args: any[]) => {
    if (window.gtag && cookieUtils.getCookie(CONSENT_STORAGE_KEY)) {
        const consent = JSON.parse(cookieUtils.getCookie(CONSENT_STORAGE_KEY) || '{}');
        if (consent.analytics) {
            window.gtag(...args);
        }
    }
};

// Facebook Pixel integration
export const fbq = (...args: any[]) => {
    if (window.fbq && cookieUtils.getCookie(CONSENT_STORAGE_KEY)) {
        const consent = JSON.parse(cookieUtils.getCookie(CONSENT_STORAGE_KEY) || '{}');
        if (consent.marketing) {
            window.fbq(...args);
        }
    }
};

// Extend window type for TypeScript
declare global {
    interface Window {
        gtag: (...args: any[]) => void;
        fbq: (...args: any[]) => void;
    }
} 